import Link from 'next/link'
import { RefObject } from 'react'
import { Dropdown, Nav } from 'react-bootstrap'
import { UserList } from 'web_core/assets/user'
import { UserInfo } from 'web_core/assets/user/Info'
import { ESUser } from 'web_core/elastic_search'
import { Profile } from 'web_core/firebase'
import { useElementSize, useMedia } from 'web_core/hooks'
import { useLayoutProperty } from '~/context/layout_property'
import { ProfileLayoutPropertyProvider } from '~/context/profile_layout_property'
import { DefaultLayout } from './Default'

type ProfileNavTab =
  | 'home'
  | 'articles'
  | 'books'
  | 'good_articles'
  | 'good_books'

type NavProps = {
  activeTab: ProfileNavTab
  profile: Profile
}

export const ProfileNav = ({ activeTab, profile }: NavProps) => (
  <Nav variant="tabs">
    <Nav.Item>
      <Link href={`/users/${profile.id}`} passHref>
        <Nav.Link active={activeTab === 'home'}>ホーム</Nav.Link>
      </Link>
    </Nav.Item>
    <Nav.Item>
      <Link href={`/users/${profile.id}/articles`} passHref>
        <Nav.Link active={activeTab === 'articles'}>
          <span className="me-1">記事</span>
          <span className={activeTab === 'articles' ? 'text-muted' : ''}>
            {profile.contents_aggregation.articles_count}
          </span>
        </Nav.Link>
      </Link>
    </Nav.Item>
    <Nav.Item>
      <Link href={`/users/${profile.id}/books`} passHref>
        <Nav.Link active={activeTab === 'books'}>
          <span className="me-1">本</span>
          <span className={activeTab === 'books' ? 'text-muted' : ''}>
            {profile.contents_aggregation.books_count}
          </span>
        </Nav.Link>
      </Link>
    </Nav.Item>
    <Nav.Item>
      <Nav.Link
        active={activeTab === 'good_articles' || activeTab === 'good_books'}
      >
        <Dropdown>
          <Dropdown.Toggle variant="link">高評価</Dropdown.Toggle>
          <Dropdown.Menu>
            <Link href={`/users/${profile.id}/good_articles`} passHref>
              <Dropdown.Item>記事</Dropdown.Item>
            </Link>
            <Link href={`/users/${profile.id}/good_books`} passHref>
              <Dropdown.Item>本</Dropdown.Item>
            </Link>
          </Dropdown.Menu>
        </Dropdown>
      </Nav.Link>
    </Nav.Item>
  </Nav>
)

type ContentsMenuProps = {
  profileNavRef: RefObject<HTMLDivElement>
  activeTab: ProfileNavTab
  profile: Profile
  teams: ESUser[]
  children: React.ReactNode
}

const ContentsMenu = ({
  profileNavRef,
  activeTab,
  profile,
  teams,
  children,
}: ContentsMenuProps) => {
  const isLg = useMedia('lg', true)
  const { headerHeight } = useLayoutProperty()
  return (
    <>
      {/* DefaultLayout の余白を打ち消すためにマイナスマージンを指定 */}
      <div className="d-flex flex-column flex-lg-row gap-0 gap-lg-7 my-n2 my-sm-n3 mx-n2 mx-md-n4">
        <div className="flex-shrink-0">
          {/* 自己紹介文(profile.comment) が無い時に width が縮むこと防止のため minWidth を指定 */}
          <div
            className={`px-2 px-md-4 ps-lg-4 pe-lg-0 pt-2 ${
              isLg ? 'position-sticky' : ''
            }`}
            style={{
              minWidth: 350,
              maxWidth: isLg ? 350 : 'none',
              top: headerHeight,
            }}
          >
            <UserInfo profile={profile} profileCommentTruncate={false} />
            {teams.length > 0 && (
              <div className="border rounded p-3 mt-4">
                <div className="fw-bold mb-2">参加チーム</div>
                <UserList
                  profiles={teams}
                  isFollowButton={false}
                  imageSize="30px"
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex-grow-1">
          <div
            ref={profileNavRef}
            className="position-sticky bg-white px-2 px-md-4 ps-lg-0 pe-lg-4 py-3"
            style={{ top: headerHeight }}
          >
            <ProfileNav activeTab={activeTab} profile={profile} />
          </div>
          <div className="px-2 px-md-4 ps-lg-0 pe-lg-4 mb-7">{children}</div>
        </div>
      </div>
    </>
  )
}

type Props = {
  title: string
  description: string
  activeTab: ProfileNavTab
  profile: Profile
  teams: ESUser[]
  children: React.ReactNode
  index: boolean
}

export const ProfileLayout = ({
  title,
  description,
  activeTab,
  profile,
  teams,
  index,
  children,
}: Props) => {
  const { elementRef: profileNavRef, height: profileNavHeight } =
    useElementSize()
  return (
    <DefaultLayout
      head={{
        title,
        description,
        keyword: profile.display_name,
        index,
        image: profile.image?.url ?? undefined,
      }}
      maxWidth={1500}
    >
      <ProfileLayoutPropertyProvider
        value={{
          profileNavHeight,
        }}
      >
        <ContentsMenu
          teams={teams}
          profileNavRef={profileNavRef}
          activeTab={activeTab}
          profile={profile}
        >
          {children}
        </ContentsMenu>
      </ProfileLayoutPropertyProvider>
    </DefaultLayout>
  )
}
